"use client";

import React from "react";
import type { DropdownMenuProps } from '@radix-ui/react-dropdown-menu';

import { FontFamilyPlugin } from "@udecode/plate-font/react";
import { useEditorPlugin, useEditorSelector } from "@udecode/plate/react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
  useOpenState,
} from './dropdown-menu';
import { ToolbarButton } from './toolbar';

const DEFAULT_FONT_FAMILY = "Georgia";

// Writer-friendly fonts that are commonly available and great for writing
const fontFamilyItems = [
  { label: "Georgia", value: "Georgia" },
  { label: "Calisto MT", value: "Calisto MT" },
  { label: "Garamond", value: "Garamond" },
  { label: "Times New Roman", value: "Times New Roman" },
  { label: "Book Antiqua", value: "Book Antiqua" },
  { label: "Palatino", value: "Palatino" },
  { label: "Crimson Text", value: "Crimson Text" },
  { label: "EB Garamond", value: "EB Garamond" },
  { label: "Libre Baskerville", value: "Libre Baskerville" },
  { label: "Minion Pro", value: "Minion Pro" },
  { label: "Arial", value: "Arial" },
  { label: "Helvetica", value: "Helvetica" },
  { label: "Calibri", value: "Calibri" },
  { label: "Open Sans", value: "Open Sans" },
  { label: "Serif", value: "serif" },
  { label: "Sans-serif", value: "sans-serif" },
];

export function FontFamilyToolbarButton(props: DropdownMenuProps) {
  const { api, editor } = useEditorPlugin(FontFamilyPlugin);
  const openState = useOpenState();

  const value = useEditorSelector((editor) => {
    const marks = api.marks();
    const fontFamily = marks?.[FontFamilyPlugin.key];
    return fontFamily as string || DEFAULT_FONT_FAMILY;
  }, []);

  const selectedItem = React.useMemo(
    () =>
      fontFamilyItems.find((item) => item.value === value) ?? fontFamilyItems[0],
    [value]
  );

  return (
    <DropdownMenu modal={false} {...openState} {...props}>
      <DropdownMenuTrigger asChild>
        <ToolbarButton
          className="min-w-[130px]"
          pressed={openState.open}
          tooltip="Font Family"
          isDropdown
        >
          <span style={{ fontFamily: selectedItem.value }}>
            {selectedItem.label}
          </span>
        </ToolbarButton>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        className="ignore-click-outside/toolbar min-w-0"
        onCloseAutoFocus={(e) => {
          e.preventDefault();
          editor.tf.focus();
        }}
        align="start"
      >
        <DropdownMenuRadioGroup
          value={value}
          onValueChange={(fontFamily) => {
            editor.tf.addMarks({ [FontFamilyPlugin.key]: fontFamily });
          }}
          label="Font Family"
        >
          {fontFamilyItems.map(({ label, value: itemValue }) => (
            <DropdownMenuRadioItem
              key={itemValue}
              className="min-w-[180px]"
              value={itemValue}
            >
              <span style={{ fontFamily: itemValue }}>
                {label}
              </span>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
